import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTheme } from '@/contexts/ThemeContext';
import { MapPin, Loader2 } from 'lucide-react';

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Enterprise emerald node icon (sharp / tactical)
const createNodeIcon = (nodeCount = 1) => {
  return L.divIcon({
    className: 'custom-node-icon',
    html: `
      <div style="position: relative; display: flex; align-items: center; justify-content: center;">
        <div style="position: absolute; width: 36px; height: 36px; border-radius: 9999px; background: rgba(16,185,129,0.25); animation: ping-live 2s cubic-bezier(0,0,0.2,1) infinite;"></div>
        <div style="
          position: relative;
          width: 22px;
          height: 22px;
          background: #10b981;
          border: 2px solid rgba(16,185,129,0.9);
          box-shadow: 0 0 0 2px rgba(0,0,0,0.6), 0 0 12px rgba(16,185,129,0.55);
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 2px;
        ">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#0b1b14" stroke-width="3">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        </div>
        ${nodeCount > 1 ? `
          <div style="
            position: absolute;
            top: -6px;
            right: -10px;
            background: #0f172a;
            color: #10b981;
            border: 1px solid rgba(16,185,129,0.6);
            padding: 0 4px;
            min-width: 16px;
            height: 16px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 10px;
            font-weight: 700;
            font-family: 'JetBrains Mono', monospace;
            border-radius: 2px;
          ">×${nodeCount}</div>
        ` : ''}
      </div>
    `,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
    popupAnchor: [0, -20],
  });
};

const MapBoundsFitter = ({ nodes }) => {
  const map = useMap();
  useEffect(() => {
    if (nodes && nodes.length > 0) {
      const bounds = L.latLngBounds(nodes.map(node => [parseFloat(node.lat), parseFloat(node.lon)]));
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [nodes, map]);
  return null;
};

const NodeMap = () => {
  const { language } = useLanguage();
  const { theme } = useTheme();
  const [nodes, setNodes] = useState([]);
  const [groupedNodes, setGroupedNodes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const translations = {
    tr: {
      title: 'Node Lokasyonları',
      description: 'Dünya genelindeki Threat.live node\'ları',
      loading: 'Harita yükleniyor...',
      error: 'Node verileri yüklenemedi',
      nodeCount: 'Node Sayısı',
      datacenter: 'Veri Merkezi',
      sponsor: 'Sponsor',
      nodes: 'node',
    },
    en: {
      title: 'Node Locations',
      description: 'Threat.live nodes around the world',
      loading: 'Loading map...',
      error: 'Failed to load node data',
      nodeCount: 'Node Count',
      datacenter: 'Datacenter',
      sponsor: 'Sponsor',
      nodes: 'nodes',
    },
  };

  const txt = translations[language] || translations.en;

  const groupNodesByLocation = (rawNodes) => {
    const locationGroups = {};
    rawNodes.forEach(node => {
      const locationKey = `${parseFloat(node.lat).toFixed(5)}_${parseFloat(node.lon).toFixed(5)}`;
      if (!locationGroups[locationKey]) {
        locationGroups[locationKey] = { nodes: [], datacenters: new Set(), datacenterNodes: {} };
      }
      locationGroups[locationKey].nodes.push(node);
      locationGroups[locationKey].datacenters.add(node.datacenter);
      if (!locationGroups[locationKey].datacenterNodes[node.datacenter]) {
        locationGroups[locationKey].datacenterNodes[node.datacenter] = 0;
      }
      locationGroups[locationKey].datacenterNodes[node.datacenter]++;
    });
    const grouped = [];
    for (const locationKey in locationGroups) {
      const group = locationGroups[locationKey];
      const firstNode = group.nodes[0];
      const groupedNode = { ...firstNode };
      groupedNode.nodeCount = group.nodes.length;
      groupedNode.originalIds = group.nodes.map(n => n.id);
      groupedNode.datacenters = Array.from(group.datacenters);
      groupedNode.datacenterCounts = groupedNode.datacenters.map(dc => group.datacenterNodes[dc]);
      const nodeWithSponsor = group.nodes.find(n => n.sponsorImg && n.sponsorImg.trim() !== '');
      if (nodeWithSponsor) groupedNode.sponsorImg = nodeWithSponsor.sponsorImg;
      grouped.push(groupedNode);
    }
    return grouped;
  };

  useEffect(() => {
    const fetchNodes = async () => {
      setIsLoading(true);
      try {
        const response = await fetch('https://api.threat.live/get_nodes');
        if (!response.ok) throw new Error('Failed to fetch nodes');
        const data = await response.json();
        setNodes(data);
        setGroupedNodes(groupNodesByLocation(data));
      } catch (err) {
        setError(err.message);
        console.error('Error fetching nodes:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchNodes();
  }, []);

  const tileUrl = theme === 'dark'
    ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
    : 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';

  const uniqueCountries = [...new Set(nodes.map(n => n.country).filter(Boolean))].length;

  const Shell = ({ children }) => (
    <div className="border border-border bg-card" data-testid="node-map">
      <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-muted/30">
        <div className="flex items-center gap-2">
          <MapPin className="h-3.5 w-3.5 text-primary" strokeWidth={2.25} />
          <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-foreground">
            {txt.title}
          </span>
          <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground hidden md:inline">
            // {txt.description}
          </span>
        </div>
        <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span className="status-dot" />
            LIVE
          </span>
          <span className="text-border">|</span>
          <span>
            <span className="text-foreground tabular">{nodes.length}</span> {txt.nodes}
          </span>
          {uniqueCountries > 0 && (
            <>
              <span className="text-border">|</span>
              <span>
                <span className="text-foreground tabular">{uniqueCountries}</span> COUNTRIES
              </span>
            </>
          )}
        </div>
      </div>
      {children}
    </div>
  );

  if (isLoading) {
    return (
      <Shell>
        <div className="h-[500px] bg-muted/20 flex items-center justify-center">
          <div className="flex items-center gap-2 text-muted-foreground font-mono text-xs uppercase tracking-wider">
            <Loader2 className="h-4 w-4 animate-spin" />
            {txt.loading}
          </div>
        </div>
      </Shell>
    );
  }

  if (error) {
    return (
      <Shell>
        <div className="h-[500px] bg-muted/20 flex items-center justify-center">
          <p className="text-destructive font-mono text-xs uppercase tracking-wider">{txt.error}</p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="h-[500px] overflow-hidden">
        <MapContainer
          center={[39, 35]}
          zoom={3}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
          attributionControl={false}
        >
          <TileLayer url={tileUrl} />
          <MapBoundsFitter nodes={groupedNodes} />
          {groupedNodes.map((node) => (
            <Marker
              key={node.id}
              position={[parseFloat(node.lat), parseFloat(node.lon)]}
              icon={createNodeIcon(node.nodeCount)}
            >
              <Popup>
                <div className="text-center p-2 min-w-[180px]">
                  <h3 className="font-bold text-base mb-1">
                    {node.city}, {node.country}
                  </h3>
                  <div className="text-sm text-gray-600 space-y-1">
                    {node.nodeCount > 1 ? (
                      node.datacenters.map((dc, idx) => (
                        <div key={dc}>
                          {dc}: {node.datacenterCounts[idx]} node
                        </div>
                      ))
                    ) : (
                      <div>{node.datacenter}</div>
                    )}
                  </div>
                  <div className="mt-2 pt-2 border-t border-gray-200">
                    <span className="text-xs text-gray-400">{txt.nodeCount}:</span>
                    <span className="ml-1 font-bold text-emerald-600">{node.nodeCount}</span>
                  </div>
                  {node.sponsorImg && (
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <span className="text-xs text-gray-400">{txt.sponsor}:</span>
                      <img
                        src={node.sponsorImg}
                        alt="Sponsor"
                        className="max-w-[100px] max-h-[40px] mx-auto mt-1"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
    </Shell>
  );
};

export default NodeMap;
